import React, { useContext, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../AppContext';
import { Link } from 'react-router-dom';

const Title: React.FC = () => {
  const navigate = useNavigate();
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('Step3 must be used within an AppProvider');
  }

  const { appData, updateAppData } = context;
  
  const [iconPreview, setIconPreview] = useState(appData.icon || 'https://build.zas.media/images/icon-app.png');

  const initialValues = {
    title: appData.title || '',
    storeTitle: appData.storeTitle || '',
    shortDescription: appData.shortDescription || '',
    description: appData.description || '',
    icon: appData.icon || '',
  };

  const validationSchema = Yup.object({
    title: Yup.string().required('Título é obrigatório'),
    icon: Yup.string().required('Ícone é obrigatório'), 
    storeTitle: Yup.string().max(30, 'Máximo de 30 caracteres').required('Título do app na loja é obrigatório'),
    shortDescription: Yup.string().max(80, 'Máximo de 80 caracteres').required('Breve descrição é obrigatória'),
    description: Yup.string().max(300, 'Máximo de 300 caracteres').required('Descrição completa é obrigatória'),
  });

  const handleIconUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: string) => void
  ) => {
    const file = event.target.files?.[0];
  
    if (!file) return;
  
    if (file.type !== 'image/png') {
      alert('Por favor, envie um arquivo PNG.');
      return;
    }
  
    if (file.size > 2 * 1024 * 1024) {
      alert('O arquivo deve ter no máximo 2MB.');
      return;
    }
  
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      
      setIconPreview(result); // ✅ Atualiza a pré-visualização
      setFieldValue('icon', result); // ✅ Atualiza o valor no Formik
      updateAppData({ icon: result }); // ✅ Salva no contexto global
    };
  
    reader.readAsDataURL(file);
  };

  const UpdatePageElements = ({ values }: { values: { title: string; shortDescription: string; storeTitle: string; icon: string; } }) => {
    React.useEffect(() => {
      const appTitleElement = document.querySelector(".app-title");
      if (appTitleElement) {
        appTitleElement.textContent = values.title;
      }
  
      const appStoreTitleElements = document.querySelectorAll(".app-store-title");
      appStoreTitleElements.forEach((element) => {
        element.textContent = values.storeTitle;
      });
  
      const appDescElements = document.querySelectorAll(".app-store-text");
      appDescElements.forEach((element) => {
        element.textContent = values.shortDescription;
      });
  
      setIconPreview(values.icon || "https://build.zas.media/images/icon-app.png");
    }, [values]);
  
    return null; // Este componente não renderiza nada, apenas executa o efeito
  };

  return (
    <div className="container">
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 content">
          <p className="font-semibold tracking-tight text-pretty text-gray-900 sm:text-2xl lg:text-balance">
            Informações do App
          </p>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              updateAppData(values);
              navigate(`/${appData.id}/logo`);
            }}
          >
            {({ setFieldValue, values }) => (
              <>
                <UpdatePageElements values={values} />
                <Form>
                  <div className="col-span-full mt-6">
                    <label htmlFor="title" className="block font-bold text-base text-gray-900">Título do App</label>
                    <div className="mt-2">
                      <Field
                        type="text"
                        name="title"
                        maxLength={24}
                        className="blockrounded-md w-96 bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-1 focus:-outline-offset-1 sm:text-sm/6"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = e.target.value;
                          setFieldValue('title', value);
                          updateAppData({ title: value });
                        }}
                      />
                      <ErrorMessage name="title" component="p" className="text-red-500 text-xs italic mt-2" />
                    </div>
                  </div>
                  <div className="col-span-full mt-6">
                    <label htmlFor="icon" className="block font-bold text-base text-gray-900">Ícone</label>
                    <div className="mt-2 flex items-center gap-x-3 submit-icon">
                      <img src={iconPreview} alt="Icone" className="app-icon" />
                      <div>
                        <span className='text-base text-gray-900'>Envie um arquivo de imagem PNG com no máximo 2MB de tamanho.</span>
                          <input
                            type="file"
                            accept="image/png"
                            className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50 mt-2"
                            onChange={(event) => handleIconUpload(event, setFieldValue)}
                          />
                      </div>
                    </div>
                    <ErrorMessage name="icon" component="p" className="text-red-500 text-xs italic mt-2" />
                  </div>
                  <hr className='mt-6' />
                  <p className="font-semibold tracking-tight text-pretty text-gray-900 sm:text-2xl lg:text-balance mt-6">
                    Informações do App nas Lojas Play Store e App Store
                  </p>
                  <div className="col-span-full mt-6">
                    <label htmlFor="app-store-title" className="block font-bold text-base text-gray-900">Título do App na Loja <small>(máx 30 caracteres)</small></label>
                    <div className="mt-2">
                      <Field
                        type="text"
                        name="storeTitle"
                        maxLength={30}
                        className="block rounded-md w-96 bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-1 focus:-outline-offset-1 sm:text-sm/6"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = e.target.value;
                          setFieldValue('storeTitle', value);
                          updateAppData({ storeTitle: value });
                        }}
                      />
                      <ErrorMessage name="storeTitle" component="p" className="text-red-500 text-xs italic mt-2" />
                    </div>
                  </div>
                  <div className="col-span-full mt-6">
                    <label htmlFor="app-store-text" className="block font-bold text-base text-gray-900">Breve Descrição <small>(máx 80 caracteres)</small></label>
                    <div className="mt-2">
                      <Field type="text" name="shortDescription" maxLength={80} className="blockrounded-md w-full bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-1 focus:-outline-offset-1 sm:text-sm/6" onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;
                        setFieldValue('shortDescription', value);
                        updateAppData({ shortDescription: value })
                      }}/>
                      <ErrorMessage name="shortDescription" component="p" className="text-red-500 text-xs italic mt-2" />
                    </div>
                  </div>
                  <div className="col-span-full mt-6">
                    <label htmlFor="app-description" className="block font-bold text-base text-gray-900">Descrição Completa</label>
                    <div className="mt-2">
                      <Field as="textarea" name="description" maxLength={500} rows={4} className="blockrounded-md w-full bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-1 focus:-outline-offset-1 sm:text-sm/6" onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue('description', e.target.value);
                        updateAppData({ description: e.target.value });
                      }}/>
                      <ErrorMessage name="description" component="p" className="text-red-500 text-xs italic mt-2" />
                    </div>
                  </div>

                  <div className="flex justify-between mt-6">
                    <Link
                      to={`/${appData.id}/tipo`}
                      className="bg-primary text-white px-6 py-3 text-xl rounded-md flex items-center space-x-2"
                    >
                      <i className="fas fa-arrow-left"></i>
                      <span>Tipo</span>
                    </Link>
                    <button
                      type="submit"
                      className="bg-primary text-white px-6 py-3 text-xl rounded-md flex items-center space-x-2"
                    >
                      <span>Logotipo</span>
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </Form>
                </>
            )}
          </Formik>
        </div>
        <div className="col-span-1 p-4 relative">
          <svg fill="none" className="dash"><defs><pattern x="0" y="0" id="pattern-dfa52911-fec3-4817-ac8e-19f444cb2809" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M-3 13 15-5M-5 5l18-18M-1 21 17 3"></path></pattern></defs><rect fill="url(#pattern-dfa52911-fec3-4817-ac8e-19f444cb2809)" width="100%" height="100%" stroke="none"></rect></svg>
          <div className='dash-over'>
            <div className="mockup-crop mt-10">
              <div className="mockup-phone">
              <div className="camera"></div>
                <div className="display">
                  <div className="artboard apps phone-1 app-gradient">
                    <div className='icon'>
                      <img src='https://build.zas.media/images/icon-gmail.png' alt='Icone' />
                      <span>Gmail</span>
                    </div>
                    <div className='icon'>
                      <img src='https://build.zas.media/images/icon-calculator.png' alt='Icone' />
                      <span>Calculadora</span>
                    </div>
                    <div className='icon'>
                      <img src='https://build.zas.media/images/icon-google-play.png' alt='Icone' />
                      <span>Play Store</span>
                    </div>
                    <div className='icon'>
                      <img src='https://build.zas.media/images/icon-phone.png' alt='Icone' />
                      <span>Phone</span>
                    </div>
                    <div className='icon'>
                      <img src='https://build.zas.media/images/icon-whatsapp.png' alt='Icone' />
                      <span>WhatsApp</span>
                    </div>
                    <div className='icon'>
                      <div className='bg-icon bg-white'>
                        <img src={iconPreview} alt='Icone' className='app-icon' />
                      </div>
                      <span className='app-title'>Seu App</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mockup-crop mt-10">
              <div className="mockup-phone">
                <div className="camera">
                  <div className='text-white text-center'>Android</div>
                </div>
                <div className="display pb-10">
                  <div className="phone-1 bg-white play-store store">
                    <div className="flex gap-1">
                      <div className='icon flex-none'>
                        <img src={iconPreview} alt='Icone' className='app-icon '/>
                      </div>
                      <p className="flex-1 block font-bold text-base text-gray-900 app-store-title">Título do App na Google Play Store</p>
                    </div>
                    <button className='bg-blue-800 w-full hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full mt-4'>Instalar</button>
                    <p className="flex-1 block text-base text-gray-900 app-store-text mt-4 text-sm">Pequena descrição do app com no máximo 80 caracteres do aplicativo na Play Store</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mockup-crop mt-10">
              <div className="mockup-phone">
                <div className="camera">
                  <div className='text-white text-center'>iPhone</div>
                </div>
                <div className="display pb-10">
                  <div className="phone-1 bg-white app-store store">
                    <div className="flex gap-1">
                      <div className='icon flex-none'>
                        <img src={iconPreview} alt='Icone' className='app-icon '/>
                      </div>
                      <div className='flex flex-col w-full'>
                        <p className="flex-1 block font-bold text-base text-gray-900 app-store-title">Título do App na App Store</p>
                        <div className='flex justify-between items-center'>
                          <button className='bg-blue-800 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full mt-1'>Obter</button>
                          <i className="fas fa-upload text-blue-800"></i>
                        </div>
                      </div>
                    </div>
                    <p className="flex-1 block text-base text-gray-900 app-store-text mt-4 text-sm">Pequena descrição do app com no máximo 80 caracteres do aplicativo na App Store</p>
                    <br/>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Title;
